import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone, Optional } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import {
  MessagePayload,
  Messaging,
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { CONSTANTS, Constants } from 'lib-trend-core';
import Swal from 'sweetalert2';
import { ChatNotificationGateService } from '../chat-preferences/chat-notification-gate.service';
import { CHAT_PUSH_CONFIG, CHAT_SURFACE, ChatClientSurfaceEnum, ChatPushConfig } from '../chat.tokens';
import * as ChatActions from '../state/chat.actions';
import { ChatShellService } from './chat-shell.service';
import { activeChannelIdSelector } from '../state/chat.selectors';

/** postMessage type emitted by the sw on notificationclick (deep link). */
const SW_NAVIGATE_MESSAGE_TYPE = 'CHAT_PUSH_NAVIGATE';

/** Friendly pre-prompt shown at most once per browser session. */
const PRE_PROMPT_SESSION_KEY = 'trendmultiChatPushPrePromptShown';

/** Last FCM token registered with the API — read back for logout cleanup. */
const TOKEN_STORAGE_KEY = 'trendmultiChatPushToken';

/**
 * `data.type` value the notifications service stamps on a mention push
 * (`NotificationsEventEnum.CHAT_MENTION`) — the only mention signal available
 * to the client, used by the foreground notification gate.
 */
const CHAT_MENTION_EVENT = 'CHAT_MENTION';

/**
 * Internal Chat — FCM web-push reception + device-token lifecycle
 * (SPEC-2026-07-internal-chat, T-23 / RF-25 / AC-21, plan §7 + ADR-003).
 *
 * Activation is idempotent and gated by `moduleChat` + an authenticated
 * session; every missing precondition (unsupported browser, no
 * `CHAT_PUSH_CONFIG`, empty `firebase.vapidKey`, denied permission) degrades
 * silently to a debug log — push is best-effort on top of the in-app flow.
 *
 * The host application provides `CHAT_PUSH_CONFIG` (API URL, Firebase config,
 * worker URL and scope) and `CHAT_SURFACE`; the registration is tagged with that
 * surface (SPEC-2026-09-internal-chat-pwa, ADR-003) so delivery can prefer the
 * chat app. The chat app registers its messaging worker on its own scope, since
 * the Angular service worker owns `/` (plan AD-09).
 *
 * Lifecycle:
 * - permission `default` → localized pre-prompt toast (once per session)
 *   BEFORE the raw browser prompt; on grant → `getToken({ vapidKey })` →
 *   `POST /user/push-token` (idempotent `$addToSet` server-side).
 * - token refresh: the modern FCM SDK has no `onTokenRefresh` — re-calling
 *   `getToken` on every activation (i.e. each login/app start) and re-POSTing
 *   is the supported refresh path.
 * - logout → best-effort `DELETE /user/push-token` (keepalive fetch so the
 *   redirect to /login cannot cancel it) + local `deleteToken`.
 * - deep link: sw `notificationclick` postMessage → in-app navigation to the
 *   conversation; foreground pushes are suppressed when that conversation is
 *   already open, otherwise surfaced as a lightweight toast.
 */
@Injectable({ providedIn: 'root' })
export class ChatPushService {

  private initialized = false;
  private registration: ServiceWorkerRegistration | null = null;
  private messaging: Messaging | null = null;
  private activeChannelId: string | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly chatShell: ChatShellService,
    private readonly store: Store,
    private readonly zone: NgZone,
    private readonly translateService: TranslateService,
    private readonly notificationGate: ChatNotificationGateService,
    @Optional() @Inject(CONSTANTS) private readonly constants: Constants | null,
    @Optional() @Inject(CHAT_PUSH_CONFIG) private readonly config: ChatPushConfig | null,
    @Optional() @Inject(CHAT_SURFACE) private readonly surface: ChatClientSurfaceEnum | null,
  ) { }

  /**
   * Idempotent activation — called by the always-mounted chat shell (bubble
   * host via ChatBadgeService.init()) and by the /chat full view. Never
   * throws: any failure only disables push for the session.
   */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.activate().catch((error) =>
      console.debug('ChatPushService - activation skipped:', error?.message ?? error));
  }

  /**
   * Logout cleanup (best effort). Uses a keepalive fetch instead of
   * HttpClient because the caller immediately clears the session and
   * hard-redirects to /login, which would cancel an in-flight XHR.
   * Must run BEFORE AuthenticationService.logout() wipes the stored JWT.
   */
  unregisterOnLogout(): void {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!token) {
        return;
      }
      localStorage.removeItem(TOKEN_STORAGE_KEY);

      const apiUrl = this.config?.apiUrl;
      if (!apiUrl) {
        return;
      }
      const jwt = this.readStoredJwt();
      fetch(`${apiUrl}/user/push-token`, {
        method: 'DELETE',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({ token, source: this.resolveSurface() }),
      }).catch(() => { /* best effort */ });

      // Invalidate the browser-side token so a different account logging in
      // on this browser mints a fresh one (also best effort — the redirect
      // may cut it short; the server prunes dead tokens on send failure).
      if (this.messaging) {
        deleteToken(this.messaging).catch(() => { /* best effort */ });
      }
    } catch {
      /* best effort — logout must never be blocked by push cleanup */
    }
  }

  /** Notification permission of this browser, or `unsupported` where web push cannot work. */
  get permissionState(): NotificationPermission | 'unsupported' {
    if (typeof Notification === 'undefined' || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Explicit permission call-to-action (SPEC-2026-09-internal-chat-pwa, T-35). Must run
   * inside a user gesture — some platforms refuse the prompt otherwise. Resolves true
   * once the permission is granted and the registration was attempted.
   */
  async enableNotifications(): Promise<boolean> {
    if (this.permissionState === 'unsupported') {
      return false;
    }
    const permission = await Notification.requestPermission().catch(() => 'denied' as NotificationPermission);
    if (permission !== 'granted') {
      return false;
    }
    if (!this.messaging || !this.registration) {
      // Not activated yet (idempotent): activation registers the token now that it is granted.
      this.init();
      return true;
    }
    await this.registerToken(this.getVapidKey());
    return true;
  }

  // ---------------------------------------------------------------------------
  // Activation pipeline
  // ---------------------------------------------------------------------------

  private async activate(): Promise<void> {
    if (!this.isAuthenticated() || !this.hasModuleChat()) {
      return;
    }
    if (!this.config?.firebase || !this.config.serviceWorkerUrl) {
      console.debug('ChatPushService - CHAT_PUSH_CONFIG not provided - web push disabled');
      return;
    }
    if (!('serviceWorker' in navigator) || typeof Notification === 'undefined') {
      console.debug('ChatPushService - browser without service worker / Notification support - web push disabled');
      return;
    }

    const vapidKey = this.getVapidKey();
    if (!vapidKey) {
      console.debug('ChatPushService - firebase.vapidKey not configured - web push disabled');
      return;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) {
      console.debug('ChatPushService - FCM not supported in this browser - web push disabled');
      return;
    }

    // Config travels in the registration URL so the sw (a static asset) never
    // hardcodes environment values.
    const { firebase, serviceWorkerUrl, serviceWorkerScope } = this.config;
    const swUrl = `${serviceWorkerUrl}?firebaseConfig=${encodeURIComponent(JSON.stringify(firebase))}`;
    this.registration = await navigator.serviceWorker.register(
      swUrl, serviceWorkerScope ? { scope: serviceWorkerScope } : undefined);
    this.messaging = getMessaging(this.resolveFirebaseApp());

    // Deep link messages posted by the sw on notificationclick.
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) =>
      this.onServiceWorkerMessage(event));

    // Foreground pushes — suppressed for the currently open conversation.
    this.store.select(activeChannelIdSelector)
      .subscribe((channelId) => this.activeChannelId = channelId);
    onMessage(this.messaging, (payload) =>
      this.zone.run(() => this.handleForegroundMessage(payload)));

    if (Notification.permission === 'granted') {
      await this.registerToken(vapidKey);
    } else if (Notification.permission === 'default' && this.config.prePrompt !== false) {
      this.showPrePrompt(vapidKey);
    }
    // 'denied' → respect the user's choice silently.
  }

  /**
   * Friendly localized pre-prompt (Hyper-style toast, top-end) shown once per
   * session BEFORE the raw browser permission prompt. Uses `get()` (not
   * `instant()`) so early activation never renders raw keys.
   */
  private showPrePrompt(vapidKey: string): void {
    if (sessionStorage.getItem(PRE_PROMPT_SESSION_KEY)) {
      return;
    }
    sessionStorage.setItem(PRE_PROMPT_SESSION_KEY, '1');

    const keys = [
      'chat.push.prePromptTitle',
      'chat.push.prePromptBody',
      'chat.push.prePromptEnable',
      'chat.push.prePromptLater',
    ];
    this.translateService.get(keys).subscribe((labels: Record<string, string>) => {
      Swal.fire({
        title: labels['chat.push.prePromptTitle'],
        text: labels['chat.push.prePromptBody'],
        icon: 'info',
        position: 'top-end',
        backdrop: false,
        toast: true,
        timer: 15000,
        timerProgressBar: true,
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: labels['chat.push.prePromptEnable'],
        cancelButtonText: labels['chat.push.prePromptLater'],
      }).then((result) => {
        if (result.isConfirmed) {
          this.requestPermission(vapidKey);
        }
      });
    });
  }

  private requestPermission(vapidKey: string): void {
    Notification.requestPermission()
      .then((permission) => {
        if (permission === 'granted') {
          return this.registerToken(vapidKey);
        }
        return undefined;
      })
      .catch((error) =>
        console.debug('ChatPushService - permission request failed:', error?.message ?? error));
  }

  /** getToken + POST /user/push-token tagged with the surface (idempotent server-side). */
  private async registerToken(vapidKey: string): Promise<void> {
    if (!this.messaging || !this.registration) {
      return;
    }
    try {
      const token = await getToken(this.messaging, {
        vapidKey,
        serviceWorkerRegistration: this.registration,
      });
      if (!token) {
        console.debug('ChatPushService - getToken returned no token - web push disabled');
        return;
      }
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      this.http.post('/user/push-token', {
        token,
        source: this.resolveSurface(),
        platform: this.resolvePlatform(),
        standalone: this.isStandalone(),
      }).subscribe({
        error: (error) =>
          console.debug('ChatPushService - token registration failed:', error?.message ?? error),
      });
    } catch (error: any) {
      console.debug('ChatPushService - getToken failed:', error?.message ?? error);
    }
  }

  // ---------------------------------------------------------------------------
  // Reception
  // ---------------------------------------------------------------------------

  /** sw notificationclick → focus existing tab → route to the conversation. */
  private onServiceWorkerMessage(event: MessageEvent): void {
    const data = event?.data;
    if (data?.type !== SW_NAVIGATE_MESSAGE_TYPE) {
      return;
    }
    this.zone.run(() => {
      const channelId: string = data.channelId ?? '';
      if (channelId) {
        this.openConversation(channelId);
      } else {
        this.chatShell.open({});
      }
    });
  }

  /**
   * Foreground push: suppressed when the target conversation is already open
   * (the message just rendered in the pane); otherwise a lightweight toast
   * with an action to jump to the conversation. Title/body arrive already
   * localized from the backend (`chat.push.*` backend catalogs).
   *
   * The toast also passes through the client-side notification gate (T-21 ·
   * RF-28/RF-36) so a muted conversation, a mentions-only conversation without
   * a mention, or an active quiet-hours window never raises it — matching what
   * the backend already enforces for push delivery. The mention flag is derived
   * from `data.type` (`CHAT_MENTION`), the only mention signal in the payload.
   */
  private handleForegroundMessage(payload: MessagePayload): void {
    const channelId = payload?.data?.['channelId'] ?? '';
    if (channelId && channelId === this.activeChannelId) {
      return;
    }
    const mentioned = payload?.data?.['type'] === CHAT_MENTION_EVENT;
    if (channelId && !this.notificationGate.canNotify(channelId, { mentioned })) {
      return;
    }

    const title = payload?.notification?.title
      || this.translateService.instant('chat.push.foregroundTitle');
    const body = payload?.notification?.body || '';

    Swal.fire({
      title,
      text: body,
      icon: 'info',
      position: 'top-end',
      backdrop: false,
      toast: true,
      timer: 7000,
      timerProgressBar: true,
      showConfirmButton: !!channelId,
      confirmButtonText: this.translateService.instant('chat.push.open'),
    }).then((result) => {
      if (result.isConfirmed && channelId) {
        this.openConversation(channelId);
      }
    });
  }

  /** Store first (does the real work), then the shell surfaces the bubble. */
  private openConversation(channelId: string): void {
    this.store.dispatch(ChatActions.openConversation({ channelId }));
    this.chatShell.open({ channelId });
  }

  // ---------------------------------------------------------------------------
  // Session / environment helpers
  // ---------------------------------------------------------------------------

  private getVapidKey(): string {
    return this.config?.firebase?.vapidKey ?? '';
  }

  /** FirebaseService (Storage) may already have initialized the default app. */
  private resolveFirebaseApp(): FirebaseApp {
    if (getApps().length > 0) {
      return getApp();
    }
    return initializeApp((this.config?.firebase ?? {}) as FirebaseOptions);
  }

  private resolveSurface(): ChatClientSurfaceEnum {
    return this.surface ?? ChatClientSurfaceEnum.WEB_APP;
  }

  /** DESKTOP | MOBILE hint for the phone-usage metric — never an authorization input. */
  private resolvePlatform(): 'DESKTOP' | 'MOBILE' {
    const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    return coarse ? 'MOBILE' : 'DESKTOP';
  }

  private isStandalone(): boolean {
    const displayStandalone = typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches;
    return displayStandalone || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  }

  /** Same storage AbstractComponent.getCurrentUserUser() reads. */
  private readStoredSession(): any {
    try {
      const storageKey = this.constants?.['currentUser'];
      if (!storageKey) {
        return null;
      }
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private readStoredJwt(): string {
    return this.readStoredSession()?.token ?? '';
  }

  private isAuthenticated(): boolean {
    return !!this.readStoredSession()?.token;
  }

  private hasModuleChat(): boolean {
    return this.readStoredSession()?.user?.company?.modules?.moduleChat ?? false;
  }
}
